import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiLink, FiMaximize2, FiMoon, FiSun, FiX } from 'react-icons/fi';
import { FaXTwitter, FaLinkedin, FaFacebook, FaGithub } from 'react-icons/fa6';
import BlogArticleShell from './BlogArticleShell';
import { getExploreBlogs } from './blogSidebarData';

const BLOG_THEME_STORAGE_KEY = 'sr-blog-theme';

const getInitialBlogTheme = () => {
  if (typeof window === 'undefined') {
    return true;
  }

  const storedTheme = window.localStorage.getItem(BLOG_THEME_STORAGE_KEY);
  if (storedTheme === 'light') {
    return false;
  }

  return true;
};

function SectionTitle({ isDark, children }) {
  const titleText = typeof children === 'string' ? children : '';
  const titleMatch = titleText.match(/^(\d+)\)\s*(.+)$/);

  if (titleMatch) {
    const [, number, label] = titleMatch;

    return (
      <h2 className={`mb-4 flex items-center gap-3 text-2xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-slate-900'} sm:text-3xl`}>
        <span
          className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border text-sm font-semibold tracking-tight backdrop-blur-md ${
            isDark
              ? 'border-white/10 bg-white/10 text-white shadow-[0_10px_30px_-18px_rgba(255,255,255,0.35)]'
              : 'border-slate-200 bg-white/70 text-slate-900 shadow-[0_10px_30px_-18px_rgba(15,23,42,0.35)]'
          }`}
        >
          {number}
        </span>
        <span>{label}</span>
      </h2>
    );
  }

  return (
    <h2 className={`mb-4 text-2xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-slate-900'} sm:text-3xl`}>
      {children}
    </h2>
  );
}

function Card({ isDark, children, tone = 'slate' }) {
  const tones = {
    slate: 'border-slate-200 bg-slate-50 dark:border-white/10 dark:bg-white/[0.03]',
    blue: 'border-blue-200 bg-blue-50 dark:border-blue-300/20 dark:bg-blue-400/[0.08]',
    purple: 'border-purple-200 bg-purple-50 dark:border-purple-300/20 dark:bg-purple-400/[0.08]',
    amber: 'border-amber-200 bg-amber-50 dark:border-amber-300/20 dark:bg-amber-400/[0.08]',
    green: 'border-green-200 bg-green-50 dark:border-green-300/20 dark:bg-green-400/[0.08]',
  };

  return <div className={`rounded-xl border p-4 ${tones[tone]}`}>{children}</div>;
}

function BulletList({ isDark, items }) {
  return (
    <ul className={`mt-5 list-disc space-y-3 pl-6 text-base leading-relaxed ${isDark ? 'text-slate-200' : 'text-slate-700'} sm:text-lg`}>
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}

function CodeLine({ isDark, children }) {
  if (typeof children === 'string') {
    const endpointMatch = children.match(/^(GET|POST|PATCH|DELETE|PUT|WS)\s+(.+)$/);
    if (endpointMatch) {
      const [, method, route] = endpointMatch;
      const methodPalette = {
        GET: isDark ? 'text-emerald-300' : 'text-emerald-700',
        POST: isDark ? 'text-sky-300' : 'text-sky-700',
        PATCH: isDark ? 'text-amber-300' : 'text-amber-700',
        DELETE: isDark ? 'text-rose-300' : 'text-rose-700',
        PUT: isDark ? 'text-violet-300' : 'text-violet-700',
        WS: isDark ? 'text-fuchsia-300' : 'text-fuchsia-700',
      };

      return (
        <code className={`block rounded px-3 py-2 text-sm ${isDark ? 'bg-[#0f172a] text-slate-100' : 'bg-white text-slate-900'}`}>
          <span className={`font-semibold ${methodPalette[method] || (isDark ? 'text-sky-300' : 'text-sky-700')}`}>{method}</span>
          <span className={isDark ? 'text-slate-300' : 'text-slate-700'}> </span>
          <span className={isDark ? 'text-slate-100' : 'text-slate-900'}>{route}</span>
        </code>
      );
    }
  }

  return (
    <code className={`block rounded px-3 py-2 text-sm ${isDark ? 'bg-[#0f172a] text-slate-100' : 'bg-white text-slate-900'}`}>
      {children}
    </code>
  );
}

function ImagePanel({ isDark, src, alt, accent, caption, onOpen }) {
  return (
    <div className={`overflow-hidden rounded-2xl border ${accent.border} ${accent.background} ${accent.shadow}`}>
      <div className="flex items-center justify-between gap-3 px-4 pt-4">
        <div className="flex items-center gap-3">
          <span className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] ${accent.badge}`}>Flow Diagram</span>
          <span className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{caption}</span>
        </div>
        <button
          type="button"
          onClick={onOpen}
          className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] transition ${
            isDark
              ? 'border-white/15 bg-white/[0.04] text-slate-100 hover:bg-white/[0.08]'
              : 'border-slate-300 bg-white text-slate-800 hover:bg-slate-100'
          }`}
          aria-label="Open image preview"
        >
          <FiMaximize2 size={14} />
        </button>
      </div>

      <div className="p-4 pt-3">
        <img src={src} alt={alt} className="w-full rounded-2xl border border-white/10" />
      </div>
    </div>
  );
}

function CadenceSystemDesignPage() {
  const [isDark, setIsDark] = useState(getInitialBlogTheme);
  const [previewImage, setPreviewImage] = useState(null);
  const [shareCopyState, setShareCopyState] = useState('idle');

  useEffect(() => {
    document.title = 'Cadence: A System Design Deep Dive | Supan Roy';
  }, []);

  useEffect(() => {
    window.localStorage.setItem(BLOG_THEME_STORAGE_KEY, isDark ? 'dark' : 'light');
  }, [isDark]);

  const openPreview = (src, alt) => setPreviewImage({ src, alt });
  const closePreview = () => setPreviewImage(null);

  const copyTextWithFallback = async (text) => {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }

    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.setAttribute('readonly', '');
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    const copied = document.execCommand('copy');
    document.body.removeChild(textArea);
    return copied;
  };

  const handleCopyShareLink = async () => {
    try {
      const copied = await copyTextWithFallback('https://supanroy.com/blogs/cadence-system-design');
      setShareCopyState(copied ? 'success' : 'error');
      setTimeout(() => setShareCopyState('idle'), 1600);
    } catch (error) {
      setShareCopyState('error');
      setTimeout(() => setShareCopyState('idle'), 1600);
    }
  };

  const exploreBlogs = getExploreBlogs('cadence-system-design');

  const articleSections = [
    { id: 'problem-statement', title: 'Problem Statement' },
    { id: 'functional-requirements', title: 'Functional Requirements' },
    { id: 'non-functional-requirements', title: 'Non-Functional Requirements' },
    { id: 'capacity-planning', title: 'Capacity Planning' },
    { id: 'high-level-architecture', title: 'High-Level Architecture' },
    { id: 'component-design', title: 'Component-by-Component Design' },
    { id: 'api-design', title: 'API Design' },
    { id: 'read-path', title: 'Read Path' },
    { id: 'write-path', title: 'Write Path' },
    { id: 'data-model', title: 'Data Model' },
    { id: 'caching-strategy', title: 'Caching Strategy' },
    { id: 'search-strategy', title: 'Search Strategy' },
    { id: 'recommendation-strategy', title: 'Recommendation Strategy' },
    { id: 'security-design', title: 'Security Design' },
    { id: 'reliability', title: 'Reliability and Failure Handling' },
    { id: 'deployment-blueprint', title: 'Deployment Blueprint' },
    { id: 'final-note', title: 'Final Note' },
  ];

  return (
    <div
      className={`min-h-screen font-sans transition-colors duration-500 ${
        isDark
          ? 'dark bg-gradient-to-b from-[#0a0f1c] via-[#0f172a] to-[#151f33]'
          : 'bg-gradient-to-b from-slate-50 via-white to-slate-100'
      }`}
    >
      <header className="sticky top-0 z-20 border-b border-slate-300/80 bg-slate-100/95 backdrop-blur dark:border-white/10 dark:bg-[#0a1220]/90 dark:supports-[backdrop-filter]:bg-[#0a1220]/75">
        <div className="mx-auto flex w-full max-w-5xl items-center justify-between px-4 py-3 sm:px-6">
          <Link to="/" className="transition hover:opacity-90" aria-label="Go to home page">
            <img src="/favicon.svg" alt="SR Logo" className="h-8 w-8 rounded-md" />
          </Link>

          <div className="flex items-center gap-3">
            <Link
              to="/blogs"
              className="rounded-full border border-slate-300 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-slate-700 transition hover:border-slate-400 hover:bg-slate-100 dark:border-white/20 dark:text-slate-100 dark:hover:border-white/40 dark:hover:bg-white/10"
            >
              Blogs
            </Link>
            <button
              type="button"
              onClick={() => setIsDark((prev) => !prev)}
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              className={`rounded-full border p-2.5 transition ${
                isDark
                  ? 'border-amber-300/70 bg-amber-100 text-amber-700 hover:bg-amber-200'
                  : 'border-slate-800/70 bg-slate-900 text-white hover:bg-slate-800'
              }`}
            >
              {isDark ? <FiSun size={16} /> : <FiMoon size={16} />}
            </button>
          </div>
        </div>
      </header>

      <BlogArticleShell sections={articleSections} relatedBlogs={exploreBlogs}>
        <article className="animate-fade-up">
          <header className="mb-12">
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-sky-700 dark:text-sky-300/90">Technical Blog</p>
            <h1 className="text-4xl font-extrabold leading-tight tracking-tight text-slate-900 dark:text-white sm:text-5xl">
              <img
                src="/cadence-logo.svg"
                alt="Cadence logo"
                className="mr-2 inline-block h-10 w-10 align-middle"
              />
              <span className="align-middle">Designing Cadence: Behind the Infrastructure</span>
            </h1>
            <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-slate-600 dark:text-slate-300">
              <span className="font-semibold text-slate-900 dark:text-slate-100">Supan Roy</span>
              <span>15 April, 2026</span>
              <span>10 min read</span>
              <span>19:26</span>
            </div>
            <p className="mt-8 text-lg leading-relaxed text-slate-700 dark:text-slate-200">
              Cadence is a modern audio streaming platform for music and podcasts. This post explains Cadence the way a public system design blog would: requirements, scale assumptions, architecture choices, read/write flows, storage math, reliability, and production deployment strategy.
            </p>
            <p className="mt-4 text-base leading-relaxed text-slate-700 dark:text-slate-200 sm:text-lg">
              This design is grounded in Cadence&apos;s current product shape, including auth, streaming, uploads, moderation, playlists, and recommendations, and extends it into a production-ready architecture built on Docker, Cloudinary, Vercel, Supabase, and Render or Railway.
            </p>
          </header>

          <section id="problem-statement" className="mb-12 scroll-mt-24">
            <SectionTitle isDark={isDark}>1) Problem Statement</SectionTitle>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Design a production-ready streaming platform where:
            </p>
            <BulletList
              isDark={isDark}
              items={[
                'Artists upload tracks/podcast episodes.',
                'Users discover and stream audio quickly.',
                'Users maintain profiles and playlists.',
                'Admins moderate submitted content.',
                'The system supports adaptive quality and observability from day one.',
              ]}
            />
          </section>

          <section id="functional-requirements" className="mb-12 scroll-mt-24">
            <SectionTitle isDark={isDark}>2) Functional Requirements</SectionTitle>

            <Card isDark={isDark} tone="purple">
              <h3 className={`mb-3 text-xl font-semibold ${isDark ? 'text-purple-300' : 'text-purple-700'}`}>Core User Features</h3>
              <BulletList
                isDark={isDark}
                items={[
                  'Sign up, log in, recover account.',
                  'Browse trending/popular/recommended/recent content.',
                  'Search tracks, artists, albums.',
                  'Stream tracks with seek support.',
                  'Create/update/delete playlists and add/remove tracks.',
                  'Follow and unfollow artists.',
                  'Auto-apply a default Cadence Playlist cover when a custom playlist cover is not provided.',
                  'Join Jam rooms for synchronized group listening.',
                  'View and edit profile.',
                ]}
              />
            </Card>

            <div className="mt-5" />

            <Card isDark={isDark} tone="blue">
              <h3 className={`mb-3 text-xl font-semibold ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>Creator Features</h3>
              <BulletList
                isDark={isDark}
                items={[
                  'Upload audio + metadata.',
                  'Auto-extract existing embedded metadata from uploaded audio (artist name, album name, and album cover) when available.',
                  'Track upload status.',
                  'Manage own uploads from profile (tracks and albums).',
                  'Edit uploaded track/album metadata from profile.',
                ]}
              />
            </Card>

            <div className="mt-5" />

            <Card isDark={isDark} tone="slate">
              <h3 className={`mb-3 text-xl font-semibold ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>Admin Features</h3>
              <BulletList isDark={isDark} items={['View pending content.', 'Approve/reject with reason.']} />
            </Card>

            <div className="mt-5" />

            <Card isDark={isDark} tone="green">
              <h3 className={`mb-3 text-xl font-semibold ${isDark ? 'text-green-300' : 'text-green-700'}`}>System Features</h3>
              <BulletList
                isDark={isDark}
                items={[
                  'JWT-based auth with refresh tokens.',
                  'Google OAuth sign-in.',
                  'Device/session controls.',
                  'Adaptive bitrate streaming.',
                  'Lyrics pipeline with provider-first + generation fallback.',
                  'Rate limiting across text input fields to reduce brute-force and spam attempts.',
                  'Play-event tracking for recommendations.',
                  'Health checks + error/performance monitoring.',
                ]}
              />
            </Card>
          </section>

          <section id="non-functional-requirements" className="mb-12 scroll-mt-24">
            <SectionTitle isDark={isDark}>3) Non-Functional Requirements</SectionTitle>
            <BulletList
              isDark={isDark}
              items={[
                'Low startup latency: playback should begin quickly.',
                'Minimal rebuffering: quality adapts to network conditions.',
                'Operational simplicity: managed services over heavy ops.',
                'Secure media delivery: signed URLs and token-based APIs.',
                'Scale path: start monolith, scale components independently.',
              ]}
            />
          </section>

          <section id="capacity-planning" className="mb-12 scroll-mt-24">
            <SectionTitle isDark={isDark}>4) Capacity Planning (Back-of-the-envelope)</SectionTitle>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Assume:</p>
            <BulletList
              isDark={isDark}
              items={[
                '500k registered users',
                '30M songs',
                'average source file size ~3 MB at standard quality',
                '10-15 streams per active user per day',
              ]}
            />

            <h3 className={`mt-6 mb-3 text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Storage</h3>
            <Card isDark={isDark} tone="amber">
              <CodeLine isDark={isDark}>Raw audio: 30M * 3MB ~= 90TB</CodeLine>
              <div className="my-2" />
              <CodeLine isDark={isDark}>With multiple bitrates + redundancy/versioning: 2-3x =&gt; 180TB - 270TB</CodeLine>
              <p className={`mt-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Metadata: songs in a few GB range; users/playlists from sub-GB to low-GB initially.
              </p>
            </Card>

            <h3 className={`mt-6 mb-3 text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Bandwidth</h3>
            <p className={`${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Audio egress dominates cost. Even moderate daily activity can produce large monthly egress, so CDN + caching + efficient ABR are mandatory.
            </p>
          </section>

          <section id="high-level-architecture" className="mb-12 scroll-mt-24">
            <SectionTitle isDark={isDark}>5) High-Level Architecture</SectionTitle>
            <ImagePanel
              isDark={isDark}
              src="/Cadence High Level Architecture.png"
              alt="Cadence high-level architecture"
              caption="System map"
              onOpen={() => openPreview('/Cadence High Level Architecture.png', 'Cadence high-level architecture')}
              accent={{
                border: 'border-slate-200 dark:border-white/10',
                background: 'bg-slate-50 dark:bg-white/[0.03]',
                shadow: 'shadow-[0_20px_70px_-35px_rgba(15,23,42,0.28)]',
                badge: 'bg-sky-100 text-sky-700 dark:bg-sky-400/15 dark:text-sky-200',
              }}
            />
          </section>

          <section id="component-design" className="mb-12 scroll-mt-24">
            <SectionTitle isDark={isDark}>6) Component-by-Component Design</SectionTitle>

            <Card isDark={isDark} tone="purple">
              <h3 className={`mb-3 text-xl font-semibold ${isDark ? 'text-purple-300' : 'text-purple-700'}`}>6.1 Client (Web App on Vercel)</h3>
              <p className={`mb-2 font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Responsibilities:</p>
              <BulletList
                isDark={isDark}
                items={[
                  'Auth UI, discovery pages, playlist/profile management.',
                  'Playback controls and queue management.',
                  'Calling API endpoints for metadata and user actions.',
                  'Streaming media directly from CDN/Cloudinary signed URLs.',
                ]}
              />
              <p className={`mt-4 mb-2 font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Key client behaviors:</p>
              <BulletList
                isDark={isDark}
                items={[
                  'Retries idempotent metadata calls with backoff.',
                  'Uses buffered playback and quality-aware stream selection.',
                  'Sends periodic play telemetry (started, progress, completed).',
                ]}
              />
            </Card>

            <div className="mt-5" />

            <Card isDark={isDark} tone="blue">
              <h3 className={`mb-3 text-xl font-semibold ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>6.2 API Layer (Django/DRF in Docker on Render/Railway)</h3>
              <p className={`mb-2 font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Responsibilities:</p>
              <BulletList
                isDark={isDark}
                items={[
                  'Stateless API servers.',
                  'JWT validation and authorization.',
                  'Business logic: uploads, moderation, playlists, recommendations.',
                  'Signed media URL issuance.',
                ]}
              />
              <p className={`mt-4 mb-2 font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Why this works:</p>
              <BulletList
                isDark={isDark}
                items={[
                  'Cadence already fits a modular-monolith shape.',
                  'Easy to ship quickly.',
                  'Can split hot paths (stream metadata, recommendations) later if needed.',
                ]}
              />
            </Card>

            <div className="mt-5" />

            <Card isDark={isDark} tone="slate">
              <h3 className={`mb-3 text-xl font-semibold ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>6.3 Database (Supabase Postgres)</h3>
              <p className={`mb-2 font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Store relational metadata:</p>
              <BulletList
                isDark={isDark}
                items={[
                  'users, roles, sessions/devices',
                  'tracks/podcasts/albums/artists',
                  'playlists + playlist tracks',
                  'moderation status/history',
                  'play history/events (or aggregated counters)',
                ]}
              />
              <p className={`mt-4 mb-2 font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Why Postgres:</p>
              <BulletList
                isDark={isDark}
                items={[
                  'Strong consistency for metadata.',
                  'Powerful querying for search/recommend seeds.',
                  'Mature indexing and JSON support for extensibility.',
                ]}
              />
            </Card>

            <div className="mt-5" />

            <Card isDark={isDark} tone="green">
              <h3 className={`mb-3 text-xl font-semibold ${isDark ? 'text-green-300' : 'text-green-700'}`}>6.4 Media Storage & Delivery (Cloudinary + CDN)</h3>
              <p className={`mb-2 font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Store and serve:</p>
              <BulletList isDark={isDark} items={['audio objects', 'artwork/cover images']} />
              <p className={`mt-4 mb-2 font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Flow:</p>
              <BulletList
                isDark={isDark}
                items={[
                  'Creator uploads source audio.',
                  'Backend attempts embedded metadata extraction from the audio file (artist, album, cover, and related tags).',
                  'Backend validates metadata and creates a pending content row.',
                  'Creator can review/edit extracted metadata before final save when needed.',
                  'Worker pipeline generates/normalizes bitrate renditions.',
                  'Media served via signed URLs (time-limited).',
                  'Client streams directly from edge/CDN endpoints.',
                ]}
              />
              <p className={`mt-4 mb-2 font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Benefits:</p>
              <BulletList
                isDark={isDark}
                items={[
                  'Offloads binary serving from API servers.',
                  'Better global performance via edge delivery.',
                  'Easier media transformations and lifecycle controls.',
                ]}
              />
            </Card>

            <div className="mt-5" />

            <Card isDark={isDark} tone="amber">
              <h3 className={`mb-3 text-xl font-semibold ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>6.5 Cache and Queue (Redis)</h3>
              <p className={`mb-2 font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Use Redis for:</p>
              <BulletList
                isDark={isDark}
                items={[
                  'popular/trending cache keys',
                  'short-lived lookup caching',
                  'Celery broker/result backend',
                  'optional rate-limit counters',
                ]}
              />
              <p className={`mt-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                This reduces DB pressure on read-heavy endpoints.
              </p>
            </Card>

            <div className="mt-5" />

            <Card isDark={isDark} tone="slate">
              <h3 className={`mb-3 text-xl font-semibold ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>6.6 Background Workers (Celery in Docker)</h3>
              <p className={`mb-2 font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Async tasks:</p>
              <BulletList
                isDark={isDark}
                items={[
                  'bitrate rendition generation',
                  'metadata extraction',
                  'lyrics fetch from lrclib with audio-based auto-generation fallback',
                  'optional waveform generation',
                  'notification fanout',
                  'cleanup/reconciliation jobs',
                ]}
              />
              <p className={`mt-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Design rule: keep user-facing requests fast; push heavy CPU/network jobs to workers.
              </p>
            </Card>

            <div className="mt-5" />

            <Card isDark={isDark} tone="blue">
              <h3 className={`mb-3 text-xl font-semibold ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>6.7 Observability</h3>
              <p className={`mb-2 font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Minimum production baseline:</p>
              <BulletList
                isDark={isDark}
                items={[
                  'structured app logs',
                  'request latency + error-rate metrics',
                  'queue depth and task failure metrics',
                  'DB performance monitoring',
                  'distributed tracing for hot endpoints',
                ]}
              />
              <p className={`mt-4 mb-2 font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>SLIs/SLO starter set:</p>
              <BulletList
                isDark={isDark}
                items={[
                  'p95 API latency for read endpoints',
                  'playback start time',
                  'stream error rate',
                  'moderation action latency',
                ]}
              />
            </Card>

            <div className="mt-5" />

            <Card isDark={isDark} tone="purple">
              <h3 className={`mb-3 text-xl font-semibold ${isDark ? 'text-purple-300' : 'text-purple-700'}`}>6.8 Lyrics Pipeline (Provider + Fallback Generation)</h3>
              <p className={`mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Cadence lyrics strategy is two-stage:</p>
              <BulletList
                isDark={isDark}
                items={[
                  'Try provider fetch from lrclib.',
                  'If lyrics are unavailable, auto-generate from audio using an extraction/transcription script pipeline.',
                ]}
              />
              <p className={`mt-4 mb-2 font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Recommended flow:</p>
              <BulletList
                isDark={isDark}
                items={[
                  'On first lyrics request for a track: check cached lyrics in DB/Redis, query lrclib by normalized title + artist, if found store and return, if not found enqueue async generation job.',
                  'Generation job: pull source audio/rendition, run transcription/extraction script, optionally produce plain lyrics and timestamped lines, persist generated lyrics with source metadata (provider vs generated).',
                  'Subsequent requests return stored lyrics immediately.',
                ]}
              />
            </Card>

            <div className="mt-5" />

            <Card isDark={isDark} tone="green">
              <h3 className={`mb-3 text-xl font-semibold ${isDark ? 'text-green-300' : 'text-green-700'}`}>6.9 Jam (Group Listening) Service</h3>
              <p className={`mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Cadence includes Jam, a group listening mode where multiple users join a room and listen in sync.
              </p>
              <p className={`mb-2 font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Responsibilities:</p>
              <BulletList
                isDark={isDark}
                items={[
                  'room lifecycle (create/join/leave/end)',
                  'host controls (play, pause, seek, next)',
                  'synchronized playback state fanout to participants',
                  'live text chat and participant presence',
                ]}
              />
              <p className={`mt-4 mb-2 font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Suggested implementation shape:</p>
              <BulletList
                isDark={isDark}
                items={[
                  'WebSocket channel for low-latency control/state events.',
                  'room state persisted in Postgres (room metadata) + Redis (ephemeral live state).',
                  'host is the source of truth; server resolves event ordering and broadcasts canonical timeline updates.',
                ]}
              />
              <p className={`mt-4 mb-2 font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Event examples:</p>
              <CodeLine isDark={isDark}>room.joined</CodeLine>
              <div className="my-2" />
              <CodeLine isDark={isDark}>room.left</CodeLine>
              <div className="my-2" />
              <CodeLine isDark={isDark}>playback.play</CodeLine>
              <div className="my-2" />
              <CodeLine isDark={isDark}>playback.pause</CodeLine>
              <div className="my-2" />
              <CodeLine isDark={isDark}>playback.seek</CodeLine>
              <div className="my-2" />
              <CodeLine isDark={isDark}>queue.updated</CodeLine>
              <div className="my-2" />
              <CodeLine isDark={isDark}>chat.message</CodeLine>
              <p className={`mt-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Design note: for v1, keep one active host per room and server-authoritative state. For v2, add host handoff, reconnection replay, and drift correction windows.
              </p>
            </Card>
          </section>

          <section id="api-design" className="mb-12 scroll-mt-24">
            <SectionTitle isDark={isDark}>7) API Design (Representative)</SectionTitle>

            <div className="grid gap-4 sm:grid-cols-2">
              <Card isDark={isDark} tone="slate">
                <h3 className={`mb-3 text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Auth</h3>
                <div className="space-y-2 text-sm">
                  <CodeLine isDark={isDark}>POST /api/token/</CodeLine>
                  <CodeLine isDark={isDark}>POST /api/token/refresh/</CodeLine>
                  <CodeLine isDark={isDark}>POST /api/auth/register/</CodeLine>
                  <CodeLine isDark={isDark}>GET /api/auth/google/</CodeLine>
                  <CodeLine isDark={isDark}>POST /api/auth/google/callback/</CodeLine>
                  <CodeLine isDark={isDark}>GET/PATCH /api/auth/profile/</CodeLine>
                </div>
              </Card>

              <Card isDark={isDark} tone="blue">
                <h3 className={`mb-3 text-xl font-semibold ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>Artist Social</h3>
                <div className="space-y-2 text-sm">
                  <CodeLine isDark={isDark}>POST /api/artists/{'{artistId}'}/follow/</CodeLine>
                  <CodeLine isDark={isDark}>POST /api/artists/{'{artistId}'}/unfollow/</CodeLine>
                  <CodeLine isDark={isDark}>GET /api/me/following-artists/</CodeLine>
                </div>
              </Card>

              <Card isDark={isDark} tone="purple">
                <h3 className={`mb-3 text-xl font-semibold ${isDark ? 'text-purple-300' : 'text-purple-700'}`}>Music</h3>
                <div className="space-y-2 text-sm">
                  <CodeLine isDark={isDark}>GET /api/music/tracks/</CodeLine>
                  <CodeLine isDark={isDark}>GET /api/music/tracks/{'{id}'}/</CodeLine>
                  <CodeLine isDark={isDark}>GET /api/music/tracks/{'{id}'}/stream/</CodeLine>
                  <CodeLine isDark={isDark}>GET /api/music/tracks/trending/</CodeLine>
                  <CodeLine isDark={isDark}>GET /api/music/tracks/popular/</CodeLine>
                  <CodeLine isDark={isDark}>GET /api/music/recommend/</CodeLine>
                  <CodeLine isDark={isDark}>GET /api/music/recent/</CodeLine>
                  <CodeLine isDark={isDark}>POST /api/music/upload/</CodeLine>
                  <CodeLine isDark={isDark}>GET /api/music/my-uploads/</CodeLine>
                  <CodeLine isDark={isDark}>PATCH /api/music/my-uploads/{'{id}'}/</CodeLine>
                  <CodeLine isDark={isDark}>DELETE /api/music/my-uploads/{'{id}'}/</CodeLine>
                </div>
              </Card>

              <Card isDark={isDark} tone="amber">
                <h3 className={`mb-3 text-xl font-semibold ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>Moderation</h3>
                <div className="space-y-2 text-sm">
                  <CodeLine isDark={isDark}>GET /api/music/moderation/pending/</CodeLine>
                  <CodeLine isDark={isDark}>POST /api/music/moderation/{'{id}'}/approve/</CodeLine>
                  <CodeLine isDark={isDark}>POST /api/music/moderation/{'{id}'}/reject/</CodeLine>
                </div>
              </Card>

              <Card isDark={isDark} tone="green">
                <h3 className={`mb-3 text-xl font-semibold ${isDark ? 'text-green-300' : 'text-green-700'}`}>Playlists</h3>
                <div className="space-y-2 text-sm">
                  <CodeLine isDark={isDark}>GET/POST /api/playlists/</CodeLine>
                  <CodeLine isDark={isDark}>GET/PATCH/DELETE /api/playlists/{'{id}'}/</CodeLine>
                  <CodeLine isDark={isDark}>POST /api/playlists/{'{id}'}/add-track/</CodeLine>
                  <CodeLine isDark={isDark}>POST /api/playlists/{'{id}'}/remove-track/</CodeLine>
                </div>
              </Card>

              <Card isDark={isDark} tone="slate">
                <h3 className={`mb-3 text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Jam</h3>
                <div className="space-y-2 text-sm">
                  <CodeLine isDark={isDark}>POST /api/jam/rooms/</CodeLine>
                  <CodeLine isDark={isDark}>POST /api/jam/rooms/{'{roomId}'}/join/</CodeLine>
                  <CodeLine isDark={isDark}>POST /api/jam/rooms/{'{roomId}'}/leave/</CodeLine>
                  <CodeLine isDark={isDark}>GET /api/jam/rooms/{'{roomId}'}/state/</CodeLine>
                  <CodeLine isDark={isDark}>GET /api/jam/rooms/{'{roomId}'}/messages/</CodeLine>
                  <CodeLine isDark={isDark}>POST /api/jam/rooms/{'{roomId}'}/messages/</CodeLine>
                  <CodeLine isDark={isDark}>WS /ws/jam/{'{roomId}'}</CodeLine>
                </div>
              </Card>
            </div>
          </section>

          <section id="read-path" className="mb-12 scroll-mt-24">
            <SectionTitle isDark={isDark}>8) Read Path: "User presses Play"</SectionTitle>
            <ImagePanel
              isDark={isDark}
              src="/read-path-cadence.png"
              alt="Cadence read path flow"
              caption="How playback metadata and media delivery split cleanly"
              onOpen={() => openPreview('/read-path-cadence.png', 'Cadence read path flow')}
              accent={{
                border: 'border-blue-200 dark:border-blue-300/20',
                background: 'bg-blue-50 dark:bg-blue-400/[0.08]',
                shadow: 'shadow-[0_24px_80px_-42px_rgba(59,130,246,0.45)]',
                badge: 'bg-blue-100 text-blue-700 dark:bg-blue-400/15 dark:text-blue-200',
              }}
            />
            <p className={`mt-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Key points: metadata comes from API/DB; audio flows directly from media edge (not through app servers); range requests/adaptive streaming reduce buffering.
            </p>
          </section>

          <section id="write-path" className="mb-12 scroll-mt-24">
            <SectionTitle isDark={isDark}>9) Write Path: "Artist uploads a track"</SectionTitle>
            <ImagePanel
              isDark={isDark}
              src="/write-path-cadence.png"
              alt="Cadence write path flow"
              caption="Upload, extract, queue, and finalize without blocking the UI"
              onOpen={() => openPreview('/write-path-cadence.png', 'Cadence write path flow')}
              accent={{
                border: 'border-purple-200 dark:border-purple-300/20',
                background: 'bg-purple-50 dark:bg-purple-400/[0.08]',
                shadow: 'shadow-[0_24px_80px_-42px_rgba(168,85,247,0.48)]',
                badge: 'bg-purple-100 text-purple-700 dark:bg-purple-400/15 dark:text-purple-200',
              }}
            />
            <p className={`mt-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Optional optimization: direct-to-cloud upload with signed upload tokens, then backend finalization.
            </p>
          </section>

          <section id="data-model" className="mb-12 scroll-mt-24">
            <SectionTitle isDark={isDark}>10) Data Model</SectionTitle>
            <div className="grid gap-4 sm:grid-cols-2">
              <Card isDark={isDark} tone="slate">
                <h3 className={`mb-3 text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Identity and Access Domain</h3>
                <BulletList
                  isDark={isDark}
                  items={[
                    'Account profile and authentication state',
                    'Role-based permissions for listener, artist, and admin',
                    'Device/session lifecycle and revocation controls',
                  ]}
                />
              </Card>

              <Card isDark={isDark} tone="purple">
                <h3 className={`mb-3 text-lg font-semibold ${isDark ? 'text-purple-300' : 'text-purple-700'}`}>Content Catalog Domain</h3>
                <BulletList
                  isDark={isDark}
                  items={[
                    'Track, album, podcast metadata and status',
                    'Artist relationships, follows, and release ownership',
                    'Rendition and media reference lifecycle',
                  ]}
                />
              </Card>

              <Card isDark={isDark} tone="green">
                <h3 className={`mb-3 text-lg font-semibold ${isDark ? 'text-green-300' : 'text-green-700'}`}>Engagement Domain</h3>
                <BulletList
                  isDark={isDark}
                  items={[
                    'Playlists and ordered track membership',
                    'Play events for recency, popularity, and recommendation seeds',
                    'Moderation audit trail for approval and rejection actions',
                  ]}
                />
              </Card>

              <Card isDark={isDark} tone="blue">
                <h3 className={`mb-3 text-lg font-semibold ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>Realtime Collaboration Domain</h3>
                <BulletList
                  isDark={isDark}
                  items={[
                    'Jam room lifecycle and participant presence',
                    'Playback timeline synchronization state',
                    'Room messaging and host-authoritative control events',
                  ]}
                />
              </Card>
            </div>

            <div className="mt-5" />

            <Card isDark={isDark} tone="amber">
              <h3 className={`mb-3 text-lg font-semibold ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>Modeling Principle</h3>
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-700'} text-base leading-relaxed sm:text-lg`}>
                Cadence models business capabilities as bounded domains first, then maps those domains to storage structures. This keeps product evolution flexible while maintaining clear ownership of data and workflows.
              </p>
            </Card>
          </section>

          <section id="caching-strategy" className="mb-12 scroll-mt-24">
            <SectionTitle isDark={isDark}>11) Caching Strategy</SectionTitle>
            <Card isDark={isDark} tone="green">
              <p className={`mb-2 font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Cache these first:</p>
              <BulletList
                isDark={isDark}
                items={['trending lists', 'popular lists', 'genre catalogs', 'top artist/album summary cards']}
              />
            </Card>
            <div className="mt-5" />
            <Card isDark={isDark} tone="amber">
              <p className={`mb-2 font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Invalidate on:</p>
              <BulletList
                isDark={isDark}
                items={[
                  'moderation state changes',
                  'new releases',
                  'significant play count updates (or TTL-based refresh)',
                ]}
              />
            </Card>
            <p className={`mt-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Pattern: read-through cache with short TTL (30-120s) for dynamic feeds.
            </p>
          </section>

          <section id="search-strategy" className="mb-12 scroll-mt-24">
            <SectionTitle isDark={isDark}>12) Search Strategy</SectionTitle>
            <Card isDark={isDark} tone="blue">
              <p className={`mb-2 font-semibold ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>v1</p>
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Postgres-based search (ILIKE + trigram + indexed columns).
              </p>
            </Card>
            <div className="mt-5" />
            <Card isDark={isDark} tone="purple">
              <p className={`mb-2 font-semibold ${isDark ? 'text-purple-300' : 'text-purple-700'}`}>v2 (when scale/feature depth grows)</p>
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Dedicated search service (OpenSearch/Meilisearch/Elasticsearch) for typo tolerance, ranking, and faceting.
              </p>
            </Card>
          </section>

          <section id="recommendation-strategy" className="mb-12 scroll-mt-24">
            <SectionTitle isDark={isDark}>13) Recommendation Strategy</SectionTitle>
            <Card isDark={isDark} tone="green">
              <p className={`mb-2 font-semibold ${isDark ? 'text-green-300' : 'text-green-700'}`}>Early stage</p>
              <BulletList
                isDark={isDark}
                items={[
                  'genre affinity from play history',
                  'popularity-weighted candidate ranking',
                  'exclude already played/recently skipped content',
                ]}
              />
            </Card>
            <div className="mt-5" />
            <Card isDark={isDark} tone="slate">
              <p className={`mb-2 font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Later</p>
              <BulletList
                isDark={isDark}
                items={[
                  'matrix factorization or deep retrieval/ranking stack',
                  'contextual signals (time of day, session intent, device/network profile)',
                ]}
              />
            </Card>
          </section>

          <section id="security-design" className="mb-12 scroll-mt-24">
            <SectionTitle isDark={isDark}>14) Security Design</SectionTitle>
            <BulletList
              isDark={isDark}
              items={[
                'JWT access + refresh with rotation.',
                'Device session binding and revocation.',
                'Role-based authorization (listener, artist, admin).',
                'Signed media URLs (short TTL).',
                'Rate limiting and abuse controls.',
                'Text-field endpoints (auth forms, search, chat, profile edits, playlist edits, and upload metadata fields) are rate-limited to prevent brute-force abuse.',
                'Audit logging for moderation actions.',
              ]}
            />
          </section>

          <section id="reliability" className="mb-12 scroll-mt-24">
            <SectionTitle isDark={isDark}>15) Reliability and Failure Handling</SectionTitle>
            <Card isDark={isDark} tone="amber">
              <p className={`mb-2 font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Failure scenarios:</p>
              <BulletList
                isDark={isDark}
                items={[
                  'DB degraded -> serve cached trending/popular fallback.',
                  'Media provider latency spike -> retry and switch rendition.',
                  'Worker backlog growth -> autoscale workers + prioritize critical queues.',
                  'API instance failures -> load balancer health checks and rolling deploys.',
                ]}
              />
            </Card>
            <div className="mt-5" />
            <Card isDark={isDark} tone="blue">
              <p className={`mb-2 font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Resilience patterns:</p>
              <BulletList
                isDark={isDark}
                items={[
                  'timeout + retry + circuit breaking',
                  'idempotency keys for write-heavy operations',
                  'dead-letter queue for repeatedly failing tasks',
                ]}
              />
            </Card>
          </section>

          <section id="deployment-blueprint" className="mb-12 scroll-mt-24">
            <SectionTitle isDark={isDark}>16) Deployment Blueprint (Production Stack)</SectionTitle>
            <Card isDark={isDark} tone="slate">
              <h3 className={`mb-2 text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>16.1 Docker</h3>
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Containerize API + workers. Pin runtime and dependencies. Single source of truth for local/prod behavior.
              </p>
            </Card>
            <div className="mt-5" />
            <Card isDark={isDark} tone="blue">
              <h3 className={`mb-2 text-lg font-semibold ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>16.2 Vercel</h3>
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Host frontend SPA. Edge caching for static assets. Fast preview deployments per branch.
              </p>
            </Card>
            <div className="mt-5" />
            <Card isDark={isDark} tone="purple">
              <h3 className={`mb-2 text-lg font-semibold ${isDark ? 'text-purple-300' : 'text-purple-700'}`}>16.3 Render or Railway</h3>
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Deploy Django API containers and Celery workers. Managed environment variables and autoscaling knobs. Cron/scheduled jobs for maintenance tasks.
              </p>
            </Card>
            <div className="mt-5" />
            <Card isDark={isDark} tone="green">
              <h3 className={`mb-2 text-lg font-semibold ${isDark ? 'text-green-300' : 'text-green-700'}`}>16.4 Supabase</h3>
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Managed Postgres for metadata. Backups, read replicas (as needed), observability tooling.
              </p>
            </Card>
            <div className="mt-5" />
            <Card isDark={isDark} tone="amber">
              <h3 className={`mb-2 text-lg font-semibold ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>16.5 Cloudinary</h3>
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Store/transform/serve audio and images. Signed URL access and CDN edge acceleration.
              </p>
            </Card>
          </section>

          <section id="final-note" className="mb-12 scroll-mt-24">
            <SectionTitle isDark={isDark}>17) Final Note</SectionTitle>
            <Card isDark={isDark} tone="purple">
              <p className={`${isDark ? 'text-gray-100' : 'text-gray-900'} text-lg leading-relaxed`}>
                This design intentionally balances realism and ambition: it is practical enough to run today with the stack (Docker + Cloudinary + Vercel + Supabase + Render/Railway), while preserving a clear path toward larger-scale architecture as Cadence grows.
              </p>
            </Card>
          </section>

          <section className="mt-14 scroll-mt-28">
            <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Share this blog</h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
              If this post helped, share it with your network.
            </p>

            <div className="mt-5 flex flex-wrap items-center gap-2 sm:gap-3">
              <a
                href="https://x.com/intent/tweet?text=Designing%20Cadence%3A%20A%20System%20Design%20Deep%20Dive&url=https%3A%2F%2Fsupanroy.com%2Fblogs%2Fcadence-system-design"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-700 no-underline transition hover:border-slate-400 hover:bg-slate-200 hover:no-underline dark:border-white/20 dark:bg-white/[0.04] dark:text-slate-200 dark:hover:border-white/40 dark:hover:bg-white/[0.1] dark:hover:text-white"
              >
                <FaXTwitter size={15} />
                <span>Share on X</span>
              </a>

              <a
                href="https://www.linkedin.com/sharing/share-offsite/?url=https%3A%2F%2Fsupanroy.com%2Fblogs%2Fcadence-system-design"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-700 no-underline transition hover:border-slate-400 hover:bg-slate-200 hover:no-underline dark:border-white/20 dark:bg-white/[0.04] dark:text-slate-200 dark:hover:border-white/40 dark:hover:bg-white/[0.1] dark:hover:text-white"
              >
                <FaLinkedin size={15} />
                <span>Share on LinkedIn</span>
              </a>

              <a
                href="https://www.facebook.com/sharer/sharer.php?u=https%3A%2F%2Fsupanroy.com%2Fblogs%2Fcadence-system-design"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-700 no-underline transition hover:border-slate-400 hover:bg-slate-200 hover:no-underline dark:border-white/20 dark:bg-white/[0.04] dark:text-slate-200 dark:hover:border-white/40 dark:hover:bg-white/[0.1] dark:hover:text-white"
              >
                <FaFacebook size={15} />
                <span>Share on Facebook</span>
              </a>

              <button
                type="button"
                onClick={handleCopyShareLink}
                className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium no-underline transition-all duration-300 hover:no-underline ${
                  shareCopyState === 'success'
                    ? 'border-emerald-300 bg-emerald-100 text-emerald-800 scale-[1.03] dark:border-emerald-300/40 dark:bg-emerald-400/20 dark:text-emerald-200'
                    : shareCopyState === 'error'
                    ? 'border-rose-300 bg-rose-100 text-rose-800 scale-[1.03] dark:border-rose-300/40 dark:bg-rose-400/20 dark:text-rose-200'
                    : 'border-slate-300 bg-slate-100 text-slate-700 hover:border-slate-400 hover:bg-slate-200 dark:border-white/20 dark:bg-white/[0.04] dark:text-slate-200 dark:hover:border-white/40 dark:hover:bg-white/[0.1] dark:hover:text-white'
                }`}
              >
                <FiLink size={15} />
                <span>
                  {shareCopyState === 'success' ? 'Copied!' : shareCopyState === 'error' ? 'Copy failed' : 'Copy link'}
                </span>
              </button>
            </div>
          </section>

          <section className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 dark:border-white/10 dark:bg-[#0f172a]/80">
            <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Connect with me</h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
              Follow for more technical updates and projects.
            </p>

            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              <a
                href="https://x.com/supanroy0"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 no-underline transition hover:border-slate-400 hover:bg-slate-100 hover:no-underline dark:border-white/10 dark:bg-white/[0.03] dark:hover:border-white/30 dark:hover:bg-white/[0.06]"
              >
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-black text-white">
                  <FaXTwitter size={16} />
                </span>
                <span className="text-sm font-medium text-slate-800 dark:text-slate-100">X</span>
              </a>

              <a
                href="https://linkedin.com/in/supanroy"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 no-underline transition hover:border-slate-400 hover:bg-slate-100 hover:no-underline dark:border-white/10 dark:bg-white/[0.03] dark:hover:border-white/30 dark:hover:bg-white/[0.06]"
              >
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#0A66C2] text-white">
                  <FaLinkedin size={16} />
                </span>
                <span className="text-sm font-medium text-slate-800 dark:text-slate-100">LinkedIn</span>
              </a>

              <a
                href="https://facebook.com/supan.being.roy"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 no-underline transition hover:border-slate-400 hover:bg-slate-100 hover:no-underline dark:border-white/10 dark:bg-white/[0.03] dark:hover:border-white/30 dark:hover:bg-white/[0.06]"
              >
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#1877F2] text-white">
                  <FaFacebook size={16} />
                </span>
                <span className="text-sm font-medium text-slate-800 dark:text-slate-100">Facebook</span>
              </a>

              <a
                href="https://github.com/Supan-Roy"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 no-underline transition hover:border-slate-400 hover:bg-slate-100 hover:no-underline dark:border-white/10 dark:bg-white/[0.03] dark:hover:border-white/30 dark:hover:bg-white/[0.06]"
              >
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#111827] text-white">
                  <FaGithub size={16} />
                </span>
                <span className="text-sm font-medium text-slate-800 dark:text-slate-100">GitHub</span>
              </a>
            </div>
          </section>
        </article>
      </BlogArticleShell>

      {previewImage ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 px-4 py-6 backdrop-blur-sm">
          <div className="relative max-h-[92vh] w-full max-w-6xl overflow-hidden rounded-3xl border border-white/10 bg-slate-950 shadow-[0_40px_120px_-40px_rgba(0,0,0,0.8)]">
            <button
              type="button"
              onClick={closePreview}
              className="absolute right-4 top-4 z-10 inline-flex items-center justify-center rounded-full bg-white/10 p-2.5 text-white transition hover:bg-white/20"
              aria-label="Close image preview"
            >
              <FiX size={16} />
            </button>
            <div className="max-h-[92vh] overflow-auto p-4 sm:p-6">
              <img src={previewImage.src} alt={previewImage.alt} className="mx-auto h-auto w-full max-w-none rounded-2xl" />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default CadenceSystemDesignPage;
